import React, { useEffect } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getSelection, $isRangeSelection, $isTextNode, TextNode } from "lexical";

const ALLOWED_NON_LETTER_PATTERN = /^[0-9+\-*/()><= .]$/;
const LETTER_PATTERN = /^[a-zA-Z]$/;
const INSERT_INPUT_TYPES = new Set([
  "insertText",
  "insertCompositionText",
  "insertFromComposition",
  "insertFromPaste",
  "insertFromDrop",
  "insertTranspose",
  "insertReplacementText",
]);

function sanitizeText(text: string): string {
  let hasAt = false;
  let sanitized = "";

  for (const char of text) {
    if (char === "@") {
      if (!hasAt) {
        sanitized += char;
        hasAt = true;
      }
    } else if (LETTER_PATTERN.test(char)) {
      if (hasAt) {
        sanitized += char;
      }
    } else if (ALLOWED_NON_LETTER_PATTERN.test(char)) {
      sanitized += char;
    }
  }

  return sanitized;
}

export const RestrictInputPlugin: React.FC = () => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    let eventTarget: HTMLElement | null = null;

    const blockInput = (event: InputEvent) => {
      if (event.cancelable) {
        event.preventDefault();
      }
      event.stopPropagation();
      event.stopImmediatePropagation();
    };

    const handleBeforeInput = (event: InputEvent) => {
      if (!INSERT_INPUT_TYPES.has(event.inputType)) return;
      const data = event.data ?? "";
      if (!data) return;

      let isTagContext = document.querySelector('[data-tag-dropdown-popover="true"]') !== null;

      if (!isTagContext) {
        editor.getEditorState().read(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            const anchor = selection.anchor;
            const node = anchor.getNode();
            if ($isTextNode(node)) {
              isTagContext = node.getTextContent().substring(0, anchor.offset).includes("@");
            }
          }
        });
      }

      for (const char of data) {
        if (char === "@") {
          if (isTagContext) {
            blockInput(event);
            return;
          }
          isTagContext = true;
        } else if (LETTER_PATTERN.test(char)) {
          if (!isTagContext) {
            blockInput(event);
            return;
          }
        } else if (!ALLOWED_NON_LETTER_PATTERN.test(char)) {
          blockInput(event);
          return;
        }
      }
    };

    const unregisterRootListener = editor.registerRootListener((rootElement) => {
      eventTarget?.removeEventListener("beforeinput", handleBeforeInput, true);
      eventTarget = rootElement?.parentElement ?? rootElement;
      eventTarget?.addEventListener("beforeinput", handleBeforeInput, true);
    });

    const unregisterTextTransform = editor.registerNodeTransform(TextNode, (textNode) => {
      const text = textNode.getTextContent();
      const sanitized = sanitizeText(text);
      if (sanitized !== text) {
        textNode.setTextContent(sanitized);
      }
    });

    return () => {
      eventTarget?.removeEventListener("beforeinput", handleBeforeInput, true);
      unregisterRootListener();
      unregisterTextTransform();
    };
  }, [editor]);

  return null;
};
